import { useCallback, useMemo } from 'react'
import { Badge } from '@mantine/core'
import { CPG_DOCUMENT_QUERY_FIELDS } from '@behuman/core-contract'
import { useClient } from '@/hooks/useClient'
import { SelectRecord } from '@/components/ui/SelectRecord'
import type { RecordColumn, RecordSearchParams } from '@/components/ui/SelectRecord'
import { toRecordQuery, toSearchResult } from './search'
import { DOCUMENT_STATUS_COLORS, type CpgDocumentListItem } from './hooks'

function FamilyChips({ families }: { families: CpgDocumentListItem['families'] }) {
  return (
    <span>
      {families.map((f) => (
        <Badge
          key={f.familyId}
          size="xs"
          radius="sm"
          variant={f.confirmed ? 'filled' : 'default'}
          color="blue"
          className="mr-1 align-middle"
          title={f.confirmed ? 'confirmed' : `${f.assignedVia} — unconfirmed`}
        >
          {f.slug}
        </Badge>
      ))}
    </span>
  )
}

interface DocumentsTableProps {
  scope: 'review' | 'all'
  onSelect: (id: number) => void
  /** Bump to re-query (a mutation moved rows out of / into this scope). */
  reloadKey?: number
  /** Narrow to documents assigned to one family (the Families view's document list). */
  familyId?: number
  pageSize?: number
}

/**
 * The document list backing both the Review Queue (scope 'review') and the Documents
 * browser (scope 'all'): a SelectRecord in embedded-table mode — every column filterable
 * with the `= ! > <` grammar and sortable, server-side via the record-query wire shape.
 */
export function DocumentsTable({
  scope,
  onSelect,
  reloadKey = 0,
  familyId,
  pageSize = 30,
}: DocumentsTableProps) {
  const client = useClient()

  const columns = useMemo<RecordColumn<CpgDocumentListItem>[]>(() => {
    const families: RecordColumn<CpgDocumentListItem> = {
      key: 'families',
      header: 'Families',
      width: 200,
      sortable: false,
      filterable: false,
      wrap: true,
      display: (r) => r.families.map((f) => f.slug).join(', '),
      render: (r) => <FamilyChips families={r.families} />,
    }
    if (scope === 'review') {
      return [
        { key: 'title', header: 'Title', width: 300, wrap: true },
        { key: 'source', header: 'Source', width: 90 },
        { key: 'version', header: 'Version', width: 90 },
        families,
      ]
    }
    return [
      { key: 'id', header: '#', width: 60 },
      { key: 'title', header: 'Title', width: 280, wrap: true },
      { key: 'source', header: 'Source', width: 90 },
      { key: 'version', header: 'Version', width: 90 },
      {
        key: 'status',
        header: 'Status',
        width: 150,
        render: (r) => (
          <Badge color={DOCUMENT_STATUS_COLORS[r.status]} variant="light" radius="sm" size="sm">
            {r.status.replace('_', ' ')}
          </Badge>
        ),
      },
      families,
      { key: 'publishedAt', header: 'Published', width: 110 },
    ]
  }, [scope])

  const search = useCallback(
    async (params: RecordSearchParams<CpgDocumentListItem>) => {
      const res = await client.listCpgDocuments({
        ...toRecordQuery(params, CPG_DOCUMENT_QUERY_FIELDS),
        scope,
        familyId,
      })
      return toSearchResult(res)
    },
    [client, scope, familyId],
  )

  return (
    <SelectRecord<CpgDocumentListItem>
      key={reloadKey}
      columns={columns}
      getRowId={(r) => String(r.id)}
      search={search}
      onSelect={(r) => onSelect(r.id)}
      recordNoun="document"
      slug={`cpg-documents-${scope}`}
      pageSize={pageSize}
      showGlobalSearch={false}
      showSelectionBar={false}
      footerHint={null}
    />
  )
}
